import { Disclosure, Transition } from '@headlessui/react';
import { Role, rolesManipulator } from 'auth';
import dayjs from 'dayjs';
import { capitalize, lowerCase } from 'lodash';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterUsersInfiniteQuery } from '@/modules/issuer-dashboard/queries/register-users.tsx';
import {
  CalendarInput,
  CalendarInputProps,
  Select,
  SelectPaginate,
  SelectPaginateOption,
} from 'ui';
import { EventLogType } from 'api';

const isReadFilterOptions = [
  { label: 'Read', value: true },
  { label: 'Not read', value: false },
];

const typeFilterOptions = Object.values(EventLogType)
  .map(type => ({
    value: type,
    label: capitalize(lowerCase(type)),
  }))
  .map(item => ({
    ...item,
    label: item.label?.replace('unsuccessfull', 'unsuccessful'),
  }));

interface NotificationsFiltersProps {
  filters: {
    isRead?: boolean;
    type?: EventLogType;
    dates?: [string, string];
    createdBy?: SelectPaginateOption<number> | null;
  };
  onChangeIsRead: (filter?: boolean) => void;
  onChangeType: (type?: EventLogType) => void;
  onChangeDates: Dispatch<SetStateAction<[string, string] | undefined>>;
  onChangeCreatedBy: (option: SelectPaginateOption<number> | null) => void;
}

const NotificationsFilters = ({
  filters,
  onChangeIsRead,
  onChangeType,
  onChangeDates,
  onChangeCreatedBy,
}: NotificationsFiltersProps) => {
  const isInvestor = rolesManipulator.current_user_role === Role.SR_INVESTOR;
  const [params] = useSearchParams();
  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const { fetchNextPage: fetchRegisterUsers } = useRegisterUsersInfiniteQuery(
    { page: 1, page_size: 20 },
    true,
    [registerId],
  );

  const handleChangeIsRead = useCallback(
    (option: boolean | null) => {
      onChangeIsRead(option ?? undefined);
    },
    [onChangeIsRead],
  );

  const handleChangeType = useCallback(
    (option: EventLogType | null) => {
      onChangeType(option || undefined);
    },
    [onChangeType],
  );

  const handleChangeDate: CalendarInputProps['rangeOnChange'] = dates => {
    if (dates) {
      const start_date = dayjs(dates[0]).format('YYYY-MM-DD');
      const end_date = dayjs(dates[1]).format('YYYY-MM-DD');

      onChangeDates([start_date, end_date]);
      return;
    }
    onChangeDates(undefined);
  };

  const handleLoadCreatedBy = useCallback(
    async (page: number, search?: string) => {
      const response = await fetchRegisterUsers({ pageParam: { page, search } });
      const users = response?.data?.pages?.at(-1)?.data || [];

      return users.map<SelectPaginateOption<number>>(({ id, first_name, last_name, email }) => {
        const name = [first_name, last_name].filter(Boolean).join(' ').trim();
        const baseLabel = name || email || `User #${id}`;
        const label = `${baseLabel}`;

        return {
          label,
          value: id,
        };
      });
    },
    [fetchRegisterUsers],
  );

  const handleChangeCreatedBy = useCallback(
    (option: SelectPaginateOption<number> | null) => {
      onChangeCreatedBy(option);
    },
    [onChangeCreatedBy],
  );

  return (
    <div className="flex justify-end">
      <Transition
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <Disclosure.Panel static>
          <div className="flex flex-wrap gap-2 items-end">
            <Select
              isClearable
              label=""
              value={filters.isRead}
              options={isReadFilterOptions}
              onChange={handleChangeIsRead}
              placeholder="Is read"
              className="flex-1 min-w-[150px]"
            />
            <Select
              isClearable
              label=""
              value={filters.type}
              options={typeFilterOptions}
              onChange={handleChangeType}
              placeholder="Status"
              className="flex-1 min-w-[200px]"
            />
            {!isInvestor && (
              <SelectPaginate<number>
                isClearable
                withBorder={false}
                placeholder="Created by"
                cacheUniqs={[registerId]}
                value={filters.createdBy ?? null}
                onAsync={handleLoadCreatedBy}
                onChange={option =>
                  handleChangeCreatedBy(option as SelectPaginateOption<number> | null)
                }
                className="flex-1 min-w-[220px]"
                debounceTimeoutSearch={500}
              />
            )}
            <CalendarInput
              range
              allowClear
              rangeOnChange={handleChangeDate}
              disabledDate={date => date.isAfter(new Date())}
              className="flex-1 w-auto min-w-[220px] [&>div]:pb-3"
            />
          </div>
        </Disclosure.Panel>
      </Transition>
    </div>
  );
};

export default NotificationsFilters;
